import {
  createActivityCommonStore,
  createActivityStore,
  createSessionStore,
} from "@/app/activity/store"
import {
  useActivityDataQuery,
  useActivityParticipatedPlayerListQuery,
  useActivityQuery,
  useSessionListQuery,
} from "@/hooks/api/useActivityQuery"
import { useUnitQuery, usePlayersQuery } from "@/hooks/api/useSettingQuery"
import { useReadTeamDeviceListQuery } from "@/hooks/api/useTeamQuery"
import useRouter from "@/hooks/router/useRoute"
import { createGlobalStore } from "@/stores/global"
import { usePositionRank } from "@/hooks/usePositionRank"

import { Session, SessionCropData, SessionData } from "@/types/session"
import { add, sub } from "date-fns"
import { useSearchParams } from "@/hooks/useSearchParams"

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"

import "./activityDetailPage.scss"
import SessionSelector from "@/app/activity/detail/sessionSelector/SessionSelector"

import {
  ActivityDataTimelineProps,
  ActivityParticipatedPlayer,
  ActivityParticipatedPlayerType,
} from "@/types/activity"

import UpsertSessionDialog from "@/app/activity/detail/components/dialog/upsertSessionDialog/UpsertSessionDialog"
import ConfirmSaveSessionDialog from "@/app/activity/detail/components/dialog/confirmSaveSessionDialog/ConfirmSaveSessionDialog"
import ActivityDetailHeader from "@/app/activity/detail/activityDetailHeader/ActivityDetailHeader"

import SimpleBarCore from "simplebar-core"
import { Magnification } from "@/app/activity/components/timeline/activityTimeline/type"
import { cloneDeep, isEqual } from "lodash"
import ActivityDetailMode from "@/app/activity/detail/ActivityDetailMode"
import ActivityCropMode from "@/app/activity/detail/ActivityCropMode"
import {
  useCreateSessionCropMutation,
  useDeleteSessionCropMutation,
  useSessionDataItemListQuery,
} from "@/hooks/api/useSessionQuery"
import { useQueryClient } from "@tanstack/react-query"
import PlayerTempMatchingDialog from "@/app/activity/detail/components/dialog/playerTempMatchingDialog/PlayerTempMatchingDialog"
import UpsertActivityDialog from "@/app/activity/components/upsertActivityDialog/UpsertActivityDialog"
import PlayerDeleteConfirmDialog from "@/app/activity/detail/components/dialog/playerDeleteConfirmDialog/PlayerDeleteConfirmDialog"
import CopySessionDialog from "@/app/activity/detail/components/dialog/copySessionDialog/CopySessionDialog"
import {
  CropType,
  LocalSessionCropData,
  LocalSessionCropHistory,
} from "@/app/activity/detail/type.ts"
import { useTranslation } from "react-i18next"
import { ApiError } from "@/app/api/type.ts"

export default function ActivityDetailPage() {
  const activityTimelineContRef = useRef<SimpleBarCore>(null)
  // middleOfScrollBarSetter 디바운스용 — 리렌더 폭주 시 setTimeout이 겹겹이 쌓여
  // setState가 연쇄로 튀는 것(Maximum update depth 경고)을 막는다
  const scrollBarTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const params = useSearchParams()
  const [activityId, setActivityId] = useState<number>()
  const [middleOfScrollBar, setMiddleOfScrollBar] = useState<number>(0)
  const [isCropping, setIsCropping] = useState(false)
  const [isActivityDialogOpen, setIsActivityDialogOpen] = useState(false)
  const [localSessionCropHistory, setLocalSessionCropHistory] = useState<LocalSessionCropHistory[]>(
    [],
  )
  const [tempSessionCropHistory, setTempSessionCropHistory] = useState<LocalSessionCropHistory[]>(
    [],
  )
  const [isSavingCrop, setIsSavingCrop] = useState(false)
  const [shouldApplyToAllSessions, setShouldApplyToAllSessions] = useState(false)
  const [copySessionSource, setCopySessionSource] = useState<Session | null>(null)
  const [selectedPlayerList, setSelectedPlayerList] = useState<ActivityParticipatedPlayerType[]>([])

  const router = useRouter()
  const { t } = useTranslation()
  const positionRank = usePositionRank()
  const queryClient = useQueryClient()

  const { setActivity, setTempActivity } = createActivityStore((state) => ({
    setActivity: state.setActivity,
    setTempActivity: state.setTempActivity,
  }))

  const {
    setSession,
    selectedSession,
    setUpsertDialogOpen,
    isUpsertDialogOpen,
    setSessionDataList,
    temporarySession,
    setConfirmSaveDialogOpen,
    setTemporarySession,
  } = createSessionStore((state) => ({
    setSession: state.setSession,
    selectedSession: state.session,
    setUpsertDialogOpen: state.setUpsertDialogOpen,
    isUpsertDialogOpen: state.upsertDialogOpen,
    setSessionDataList: state.setSessionDataList,
    temporarySession: state.temporarySession,
    setConfirmSaveDialogOpen: state.setConfirmSaveDialogOpen,
    setTemporarySession: state.setTemporarySession,
  }))

  const { setTeamSpeedUnit, setSnackbar } = createGlobalStore((state) => ({
    setTeamSpeedUnit: state.setTeamUnit,
    setSnackbar: state.setSnackBar,
  }))
  const {
    setRange,
    timelineWidth,
    magnification,
    setMagnification,
    setPlayerRemoveDialogProps,
    sortKey,
    sortDirection,
  } = createActivityCommonStore((state) => ({
    setRange: state.setRange,
    timelineWidth: state.timelineWidth,
    magnification: state.magnification,
    setMagnification: state.setMagnification,
    setPlayerRemoveDialogProps: state.setPlayerRemoveDialogProps,
    sortKey: state.sortKey,
    sortDirection: state.sortDirection,
  }))

  const { data: sessionList } = useSessionListQuery({
    idfActivity: Number(params.get("activity") ?? 0) || 0,
  })

  const idfSessionList = useMemo(() => {
    if (sessionList) {
      return sessionList.map((session) => session.idfSession).sort((a, b) => a - b)
    }

    return []
  }, [sessionList])

  const { data: sessionDataList } = useSessionDataItemListQuery(idfSessionList)
  const { data: playerList } = useActivityParticipatedPlayerListQuery(
    [activityId as number],
    !!activityId,
  )

  const { data: activityInfo, error } = useActivityQuery(activityId)

  const { data: activityData } = useActivityDataQuery(activityId)
  const { data: unit } = useUnitQuery()
  const { data: teamDeviceList } = useReadTeamDeviceListQuery()
  const { data: allTeamPlayers } = usePlayersQuery()

  // 선택된 세션에 SessionData 레코드가 있는 idfData Set (세션에 등록된 선수)
  const sessionPlayerIdfDataSet = useMemo(() => {
    if (!selectedSession || !sessionDataList || !idfSessionList) {
      return null
    }

    const sessionIdx = idfSessionList.indexOf(selectedSession.idfSession)
    if (sessionIdx === -1) {
      return null
    }

    const sessionData = sessionDataList[sessionIdx]
    if (!sessionData) {
      return null
    }

    return new Set(sessionData.filter((sd) => sd.matchedToSession).map((sd) => sd.idfData))
  }, [selectedSession, sessionDataList, idfSessionList])

  const { mutateAsync: deleteSessionCrop, isPending: isDeletePending } =
    useDeleteSessionCropMutation()
  const { mutateAsync: createSessionCrop, isPending: isCreatePending } =
    useCreateSessionCropMutation()

  const activityDataList = useMemo(() => {
    if (!activityData || !playerList) {
      return []
    }

    const getPlayerInfo = (data: (typeof activityData)[0]) => {
      const device = data.idfPlayer
        ? teamDeviceList?.find((d) => d.idfPlayer === data.idfPlayer)
        : null
      const settingsPlayer = data.idfPlayer
        ? allTeamPlayers?.find((p) => p.idfPlayer === data.idfPlayer)
        : null
      const participated = data.idfPlayer
        ? playerList?.find((p) => p.idfPlayer === data.idfPlayer)
        : null

      const dp = data.devicePlayer

      return {
        uniformNumber:
          device?.uniformNumber ||
          device?.numberUniform ||
          device?.backNumber ||
          dp?.uniformNumber ||
          dp?.numberUniform ||
          dp?.backNumber ||
          0,
        nickname: device?.nickname || device?.nickName || dp?.nickname || dp?.nickName || "",
        position: participated?.position || data.position || settingsPlayer?.position || "",
      }
    }

    const sorted = [...activityData].sort((a, b) => {
      const playerA = getPlayerInfo(a)
      const playerB = getPlayerInfo(b)

      let compare = 0
      switch (sortKey) {
        case "uniformNumber":
          if (playerA.uniformNumber === 0 && playerB.uniformNumber !== 0) {
            return 1
          }
          if (playerB.uniformNumber === 0 && playerA.uniformNumber !== 0) {
            return -1
          }
          compare = playerA.uniformNumber - playerB.uniformNumber
          if (compare === 0) {
            compare = playerA.nickname.localeCompare(playerB.nickname)
          }
          break
        case "nickname":
          compare = playerA.nickname.localeCompare(playerB.nickname)
          break
        case "position": {
          // 백엔드 정렬 순서 신뢰(positionRank). 기존 표시(GK-우선)를 유지하기 위해 B-A로 비교.
          compare = positionRank(playerB.position) - positionRank(playerA.position)
          if (compare === 0) {
            compare = playerA.uniformNumber - playerB.uniformNumber
          }
          break
        }
      }

      return sortDirection === "desc" ? -compare : compare
    })

    if (!selectedSession || !sessionPlayerIdfDataSet) {
      return sorted
    }

    const participated: typeof sorted = []
    const notParticipated: typeof sorted = []

    sorted.forEach((data) => {
      if (sessionPlayerIdfDataSet.has(data.idfData)) {
        participated.push(data)
      } else {
        notParticipated.push(data)
      }
    })

    return [...participated, ...notParticipated]
  }, [
    activityData,
    playerList,
    allTeamPlayers,
    teamDeviceList,
    sortKey,
    sortDirection,
    selectedSession,
    sessionPlayerIdfDataSet,
    positionRank,
  ])

  const groupedActivityData = useMemo(() => {
    if (!activityDataList) {
      return []
    }

    return activityDataList.reduce((acc: ActivityDataTimelineProps[], activityData) => {
      // 매칭 안 된 선수(idfPlayer === 0)는 각각 별도로 표시
      if (activityData.idfPlayer && acc.find((item) => item.idfPlayer === activityData.idfPlayer)) {
        return acc
      }

      const filtered = activityData.idfPlayer
        ? activityDataList.filter((item) => item.idfPlayer === activityData.idfPlayer)
        : [activityData]

      const activityDataByIdfData = filtered.reduce(
        (map, item) => {
          map[item.idfData] = item

          return map
        },
        {} as Record<number, typeof activityData>,
      )

      if (filtered.length > 1) {
        const activities = {
          ...activityData,
          idfData: filtered.map((item) => item.idfData),
          activityDataByIdfData,
        }
        acc.push(activities)
      } else {
        acc.push({
          ...activityData,
          idfData: [activityData.idfData],
          activityDataByIdfData,
        })
      }

      return acc
    }, [])
  }, [activityDataList])

  // groupedActivityData에서 선수/디바이스 목록 도출 (왼쪽 리스트와 동일 소스)
  // idfPlayer가 없는 항목은 -idfData[0]을 식별자로 사용
  const allDataPlayers = useMemo<ActivityParticipatedPlayerType[]>(() => {
    if (!groupedActivityData) {
      return []
    }

    return groupedActivityData
      .filter((data) => {
        // 선수 정보가 없는 항목 제외
        const dp = data.devicePlayer

        return data.idfPlayer || dp?.nickname || dp?.nickName
      })
      .map((data) => {
        const id = data.idfPlayer || -data.idfData[0]
        const teamPlayer = data.idfPlayer
          ? allTeamPlayers?.find((p) => p.idfPlayer === data.idfPlayer)
          : null
        const participated = data.idfPlayer
          ? playerList?.find((p) => p.idfPlayer === data.idfPlayer)
          : null
        const dp = data.devicePlayer

        return {
          idfPlayer: id,
          uniformNumber:
            participated?.uniformNumber ??
            teamPlayer?.uniformNumber ??
            (dp?.uniformNumber as number) ??
            0,
          nickname:
            participated?.nickname ?? teamPlayer?.nickname ?? (dp?.nickname as string) ?? "",
          name: participated?.name ?? teamPlayer?.nickname ?? (dp?.nickname as string) ?? "",
          position: participated?.position || data.position || teamPlayer?.position || "",
          image: participated?.image ?? teamPlayer?.image ?? "",
          backNumber:
            participated?.backNumber ?? teamPlayer?.backNumber ?? (dp?.backNumber as number) ?? 0,
          playTime: participated?.playTime ?? 0,
          idfPlayerGroup: participated?.idfPlayerGroup ?? teamPlayer?.idfPlayerGroup ?? 0,
        }
      })
  }, [groupedActivityData, playerList, allTeamPlayers])

  // allDataPlayers 로드 시 전체 선수를 selectedPlayerList에 초기화
  useEffect(() => {
    if (allDataPlayers.length > 0 && selectedPlayerList.length === 0) {
      setSelectedPlayerList(allDataPlayers)
    }
  }, [allDataPlayers])

  const filteredGroupedActivityData = useMemo(() => {
    if (!groupedActivityData || !playerList) {
      return groupedActivityData
    }

    let result = groupedActivityData

    const selectedPlayerIds = new Set(selectedPlayerList.map((p) => p.idfPlayer))
    result = result.filter((data) => {
      // 선수정보 없는 항목은 항상 표시 (필터 대상 아님)
      const dp = data.devicePlayer
      if (!data.idfPlayer && !dp?.nickname && !dp?.nickName) {
        return true
      }

      const id = data.idfPlayer || -data.idfData[0]

      return selectedPlayerIds.has(id)
    })

    // 세션 선택 시: 세션에 매칭된 선수를 반드시 상단으로 배치 (SessionData idfData 기반)
    if (selectedSession && sessionPlayerIdfDataSet) {
      const participated = result.filter((data) =>
        data.idfData.some((id) => sessionPlayerIdfDataSet.has(id)),
      )
      const notParticipated = result.filter(
        (data) => !data.idfData.some((id) => sessionPlayerIdfDataSet.has(id)),
      )
      result = [...participated, ...notParticipated]
    }

    return result
  }, [
    groupedActivityData,
    playerList,
    selectedPlayerList,
    selectedSession,
    sessionPlayerIdfDataSet,
  ])

  const mergeCropHistory = (
    records: SessionCropData[],
    localCropData: LocalSessionCropData,
    actionType: CropType,
  ) => {
    switch (actionType) {
      case CropType.delete: {
        return records.filter((record) => {
          if (record.localIdfCropNumber && localCropData.localIdfCropNumber) {
            return record.localIdfCropNumber !== localCropData.localIdfCropNumber
          }

          return record.idfSessionCropRecord !== localCropData.idfSessionCropRecord
        })
      }
      default: {
        records.push({
          localIdfCropNumber: localCropData.localIdfCropNumber,
          idfSessionCropRecord: localCropData.idfSessionCropRecord || new Date().getTime(),
          cropStartTime: localCropData.cropStartTime,
          cropEndTime: localCropData.cropEndTime,
        })

        return records
      }
    }
  }

  const sessionDataMap = useMemo(() => {
    const result: Record<number, SessionData[]> = {}
    if (!sessionDataList) {
      return result
    }

    const idfInjected = sessionDataList.map((sessionData, index) => {
      return sessionData?.map((session) => {
        session.idfSession = session.idfSession ?? (idfSessionList?.[index] as number)

        return session
      })
    })

    ;(idfInjected?.flat() as SessionData[]).map((sessionData: SessionData) => {
      if (!sessionData) {
        return
      }
      const idfSession = sessionData.idfData
      if (!result[idfSession]) {
        result[idfSession] = []
      }
      result[idfSession].push(cloneDeep(sessionData))
    })

    localSessionCropHistory.filter(Boolean).forEach((localCropHistory) => {
      const { actionType, history } = localCropHistory

      history.forEach((history) => {
        const { idfData, idfSession } = history

        const targetData = result?.[idfData as number]

        if (targetData) {
          const targetSession = targetData.find((session) => session.idfSession === idfSession)

          if (targetSession) {
            const records = targetSession.cropRecords || []

            targetSession.cropRecords =
              mergeCropHistory(records, history as LocalSessionCropData, actionType) || []
          }
        }
      })
    })

    return result
  }, [sessionDataList, localSessionCropHistory])
  const historyWillRemove = useMemo(() => {
    return localSessionCropHistory
      .filter(Boolean)
      .filter((item) => item.actionType === CropType.delete)
      .map((item) => item.history)
      .flat()
      .filter((item) => !item.localIdfCropNumber)
      .map((item) => item.idfSessionCropRecord)
  }, [localSessionCropHistory])

  const historyWillRemoveLocal = useMemo(() => {
    return localSessionCropHistory
      .filter(Boolean)
      .filter((item) => item.actionType === CropType.delete)
      .map((item) => item.history)
      .flat()
      .filter((item) => item.localIdfCropNumber)
      .map((item) => item.localIdfCropNumber)
  }, [localSessionCropHistory])

  const historyWillAdd = useMemo(() => {
    return localSessionCropHistory
      .filter(Boolean)
      .filter(
        (item) =>
          item.actionType === CropType.add ||
          item.actionType === CropType.copyAll ||
          item.actionType === CropType.copy,
      )
      .map((item) => item.history)
      .flat()
      .map((item) => ({
        idfData: item.idfData,
        cropRanges: [new Date(item.cropStartTime).valueOf(), new Date(item.cropEndTime).valueOf()],
        idfSessionCropRecord: item.idfSessionCropRecord,
        idfSession: item.idfSession,
        localIdfCropNumber: item.localIdfCropNumber,
      }))
  }, [localSessionCropHistory])

  // 값이 실제로 바뀔 때만 setState — 리렌더 폭주(ResizeObserver/effect 재발화) 시 무한 setState 방지
  const setMiddleOfScrollBarIfChanged = (next: number) => {
    setMiddleOfScrollBar((prev) => (prev === next ? prev : next))
  }

  const middleOfScrollBarSetter = () => {
    // 이전 예약분을 취소해 버스트를 마지막 한 번으로 합친다 (setState 연쇄 방지)
    if (scrollBarTimeoutRef.current) {
      clearTimeout(scrollBarTimeoutRef.current)
    }
    scrollBarTimeoutRef.current = setTimeout(() => {
      const timeline = document.querySelector(".SessionTimelineCont") as HTMLElement | null
      const scrollBar = document.querySelector(
        ".simplebar-horizontal > .simplebar-scrollbar",
      ) as HTMLElement | null

      // 세션 목록 refetch 중에는 타임라인이 잠시 언마운트됨 — null 접근으로 크래시하지 않도록 가드
      if (!timeline) {
        return
      }

      if (!scrollBar || (magnification && magnification <= Magnification.INIT)) {
        setMiddleOfScrollBarIfChanged(timeline.clientWidth / 2)
      } else if (scrollBar) {
        const transform = scrollBar?.style.transform
        const width = scrollBar?.style.width

        const transformRegex = /translate3d\((\d+)px, \d+px, \d+px\)/
        const widthRegex = /(\d+)px/
        const transformMatch = transform.match(transformRegex)
        const widthMatch = width.match(widthRegex)

        if (transformMatch) {
          const value = transformMatch[1]

          if (value === "0") {
            return
          }

          const widthValue = widthMatch ? widthMatch[1] : "0"
          setMiddleOfScrollBarIfChanged(parseInt(value) + parseInt(widthValue) / 2)
        }
      }
    })
  }

  const onClickSaveCrop = async (applyToAllOverride?: boolean) => {
    if (isDeletePending || isCreatePending) {
      return
    }

    // split-button 이 setShouldApplyToAllSessions + onClickSaveCrop 를 동기 호출 →
    // 클로저는 옛 state 를 봄. override 인자가 오면 그 값을 truthy 로 간주
    const applyToAllEffective =
      typeof applyToAllOverride === "boolean" ? applyToAllOverride : shouldApplyToAllSessions

    const filteredWillRemove = historyWillRemove.filter(
      (item) => !historyWillAdd.map((add) => add.idfSessionCropRecord).includes(item),
    )

    const filteredWillAdd = historyWillAdd.filter(
      (item) =>
        !historyWillRemove.includes(item.idfSessionCropRecord) &&
        !historyWillRemoveLocal.includes(item.localIdfCropNumber) &&
        item.idfSession === selectedSession?.idfSession,
    )

    const addCropRecord: Record<number, number[][]> = {}

    filteredWillAdd.forEach((item) => {
      delete item.idfSession
      if (!addCropRecord[item.idfData]) {
        addCropRecord[item.idfData] = []
      }

      addCropRecord[item.idfData].push([item.cropRanges[0] as number, item.cropRanges[1] as number])
    })
    const dataCropList: { idfData: number; cropRanges: number[][] }[] = []

    Object.entries(addCropRecord).forEach(([idfData, cropRanges]) => {
      dataCropList.push({
        idfData: Number(idfData),
        cropRanges,
      })
    })

    try {
      if (filteredWillRemove.length) {
        const removeTargets = filteredWillRemove.filter((idfData) => idfData !== undefined)

        for (const idfSessionCropRecord of removeTargets) {
          await deleteSessionCrop({
            idfSession: selectedSession?.idfSession as number,
            idfSessionCropRecord: idfSessionCropRecord as number,
          })

          setLocalSessionCropHistory((prev) =>
            prev.filter((item) => {
              if (item.actionType !== CropType.delete) {
                return true
              }

              const hasTarget = item.history.some(
                (h) => h.idfSessionCropRecord === idfSessionCropRecord,
              )

              return !hasTarget
            }),
          )
        }
      }

      if (filteredWillAdd.length) {
        const addPromise = createSessionCrop({
          idfSession: selectedSession?.idfSession as number,
          payload: {
            idfSession: selectedSession?.idfSession as number,
            idfActivity: activityId as number,
            dataCropList,
          },
        })
        await addPromise

        // bulk 적용 시에는 전 세션 결과를 모아 하단에서 한 번만 노출 (조기 성공 스낵바 방지)
        if (!applyToAllEffective) {
          setSnackbar({
            open: true,
            message: t("messages.successUpdate"),
            severity: "success",
          })
        }
      }

      // Apply to All Sessions: save same crop ranges to overlapping sessions
      if (applyToAllEffective && dataCropList.length > 0 && sessionList && selectedSession) {
        // Deduplicate crop ranges (all players share the same time ranges)
        const uniqueRanges: number[][] = []
        const seen = new Set<string>()
        for (const d of dataCropList) {
          for (const r of d.cropRanges) {
            const key = `${r[0]}-${r[1]}`
            if (!seen.has(key)) {
              seen.add(key)
              uniqueRanges.push(r)
            }
          }
        }

        const cropStart = Math.min(...uniqueRanges.map((r) => r[0]))
        const cropEnd = Math.max(...uniqueRanges.map((r) => r[1]))

        const overlappingSessions = sessionList.filter((s) => {
          if (s.idfSession === selectedSession.idfSession) {
            return false
          }
          const sStart = new Date(s.dateStart).valueOf()
          const sEnd = new Date(s.dateFinish).valueOf()

          return sStart < cropEnd && sEnd > cropStart
        })

        const idfSessionListSorted = sessionList.map((s) => s.idfSession).sort((a, b) => a - b)

        // Only apply to players that were actually cropped in the current session
        const croppedIdfDataSet = new Set(dataCropList.map((d) => d.idfData))
        const currentSessionIdx = idfSessionListSorted.indexOf(selectedSession.idfSession)
        const currentSdList = currentSessionIdx !== -1 ? sessionDataList?.[currentSessionIdx] : null
        const croppedIdfPlayerSet = new Set(
          currentSdList
            ?.filter((sd): sd is SessionData => sd != null && croppedIdfDataSet.has(sd.idfData))
            .map((sd) => sd.idfPlayer) ?? [],
        )

        // 새 크롭 범위가 대상 세션의 기존 cropRecords 와 겹치면, 겹치는 기존 크롭을
        // 삭제하고 둘을 포함하는 합집합(union) 범위로 다시 생성한다.
        // 백엔드는 겹치는 범위를 OVERLAPPING_SESSION_CROP_RANGES 400 으로 거부하므로
        // 단순 생성이 아니라 delete → create 가 필요하다.
        const toMs = (v: number | string) => (typeof v === "number" ? v : new Date(v).valueOf())

        // 한 (idfData) 의 새 범위 + 기존 레코드를 받아, 생성할 합집합 범위와 삭제할 레코드를 계획.
        // 실제로 겹치는(strict) 구간끼리만 합치고, 새 범위가 기존 레코드 안에 완전히 포함되면 no-op.
        const planMerge = (newRanges: number[][], existing: SessionCropData[]) => {
          type Iv = { s: number; e: number; record?: SessionCropData }
          const intervals: Iv[] = [
            ...existing.map((r) => ({
              s: toMs(r.cropStartTime),
              e: toMs(r.cropEndTime),
              record: r,
            })),
            ...newRanges.map(([s, e]) => ({ s, e })),
          ].sort((a, b) => a.s - b.s)

          const clusters: Iv[][] = []
          for (const iv of intervals) {
            const last = clusters[clusters.length - 1]
            const lastEnd = last ? Math.max(...last.map((x) => x.e)) : -Infinity
            if (last && iv.s < lastEnd) {
              last.push(iv)
            } else {
              clusters.push([iv])
            }
          }

          const toCreate: number[][] = []
          const toDelete: SessionCropData[] = []
          for (const cl of clusters) {
            const hasNew = cl.some((x) => !x.record)
            if (!hasNew) {
              continue
            } // 기존 크롭만 있는 클러스터는 건드리지 않음

            const span = [Math.min(...cl.map((x) => x.s)), Math.max(...cl.map((x) => x.e))]
            const existingInCl = cl.filter((x) => x.record)
            // 새 범위가 기존 한 레코드 안에 완전히 포함 → 이미 커버됨 (변경 없음)
            const alreadyCovered =
              existingInCl.length === 1 &&
              existingInCl[0].s <= span[0] &&
              existingInCl[0].e >= span[1]
            if (alreadyCovered) {
              continue
            }

            toCreate.push(span)
            existingInCl.forEach((x) => toDelete.push(x.record as SessionCropData))
          }

          return { toCreate, toDelete }
        }

        let appliedCount = 0 // 크롭을 생성한 세션 수
        let mergedCount = 0 // 기존 크롭과 합쳐진(삭제 후 재생성) 세션 수
        let failedCount = 0 // 적용 실패 세션 수

        for (const session of overlappingSessions) {
          const sessionIdx = idfSessionListSorted.indexOf(session.idfSession)
          const sdList = sessionIdx !== -1 ? sessionDataList?.[sessionIdx] : null
          if (!sdList) {
            continue
          }

          const matched = sdList.filter(
            (sd): sd is SessionData =>
              sd != null && sd.matchedToSession && croppedIdfPlayerSet.has(sd.idfPlayer),
          )
          if (matched.length === 0) {
            continue
          }

          const dataCropList: { idfData: number; cropRanges: number[][] }[] = []
          // 삭제 대상: 실패 시 원복할 수 있도록 idfData + 원본 범위를 함께 보관
          const plannedDeletes: { idfData: number; id: number; range: number[] }[] = []

          for (const sd of matched) {
            const existing = sd.cropRecords || []
            const clampedRanges = uniqueRanges
              .map(([start, end]) => [
                Math.max(start, new Date(sd.dateStart).valueOf()),
                Math.min(end, new Date(sd.dateFinish).valueOf()),
              ])
              .filter(([s, e]) => s < e)
            if (clampedRanges.length === 0) {
              continue
            }

            const { toCreate, toDelete } = planMerge(clampedRanges, existing)
            if (toCreate.length === 0) {
              continue
            } // 이미 기존 크롭이 모두 커버

            dataCropList.push({ idfData: sd.idfData, cropRanges: toCreate })
            toDelete.forEach((rec) =>
              plannedDeletes.push({
                idfData: sd.idfData,
                id: rec.idfSessionCropRecord,
                range: [toMs(rec.cropStartTime), toMs(rec.cropEndTime)],
              }),
            )
          }

          if (dataCropList.length === 0) {
            continue
          } // 이 세션은 변경 없음

          // 실제로 삭제에 성공한 레코드만 추적 → create 실패 시 정확히 그 레코드만 원복
          const deletedDone: { idfData: number; range: number[] }[] = []
          try {
            // 1) 겹치는 기존 크롭 삭제 → 2) 합집합 범위 생성
            for (const del of plannedDeletes) {
              await deleteSessionCrop({
                idfSession: session.idfSession,
                idfSessionCropRecord: del.id,
              })
              deletedDone.push({ idfData: del.idfData, range: del.range })
            }
            await createSessionCrop({
              idfSession: session.idfSession,
              payload: {
                idfSession: session.idfSession,
                idfActivity: activityId as number,
                dataCropList,
              },
            })
            appliedCount += 1
            if (plannedDeletes.length > 0) {
              mergedCount += 1
            }
            queryClient.invalidateQueries({
              queryKey: ["readSessionDataItems", session.idfSession],
            })
          } catch {
            failedCount += 1
            // create 가 실패했고 이미 삭제한 기존 크롭이 있으면 원복 시도 (데이터 손실 방지)
            if (deletedDone.length > 0) {
              const rollbackByData = new Map<number, number[][]>()
              deletedDone.forEach(({ idfData, range }) => {
                const ranges = rollbackByData.get(idfData) ?? []
                ranges.push(range)
                rollbackByData.set(idfData, ranges)
              })
              try {
                await createSessionCrop({
                  idfSession: session.idfSession,
                  payload: {
                    idfSession: session.idfSession,
                    idfActivity: activityId as number,
                    dataCropList: Array.from(rollbackByData, ([idfData, cropRanges]) => ({
                      idfData,
                      cropRanges,
                    })),
                  },
                })
              } catch {
                // 원복도 실패 — 더 시도할 수 있는 게 없음 (실패로 집계됨)
              }
              queryClient.invalidateQueries({
                queryKey: ["readSessionDataItems", session.idfSession],
              })
            }
          }
        }

        // 결과를 사용자에게 명시적으로 노출 (silent success 방지)
        if (failedCount > 0) {
          setSnackbar({
            open: true,
            severity: "error",
            message: t("messages.cropAppliedWithFailure", {
              applied: appliedCount,
              merged: mergedCount,
              failed: failedCount,
              defaultValue:
                "Crop applied to {applied} session(s), {merged} merged, {failed} failed",
            }),
          })
        } else if (mergedCount > 0) {
          setSnackbar({
            open: true,
            severity: "success",
            message: t("messages.cropAppliedWithMerge", {
              applied: appliedCount,
              merged: mergedCount,
              defaultValue:
                "Crop applied to {applied} session(s), merged with existing crops in {merged}",
            }),
          })
        } else {
          setSnackbar({
            open: true,
            message: t("messages.successUpdate"),
            severity: "success",
          })
        }
      }

      queryClient.invalidateQueries({
        queryKey: ["readSessionDataItems", selectedSession?.idfSession],
      })

      resetLocalCropData()
      setShouldApplyToAllSessions(false)
    } catch (error: unknown) {
      const status = (error as ApiError).status || "messages.errorUpdate"
      setSnackbar({
        open: true,
        message: t(status as string),
        severity: "error",
      })
    }
  }

  const onClickApplyToAllSessions = useCallback(() => {
    setShouldApplyToAllSessions((prev) => !prev)
  }, [])

  const resetLocalCropData = () => {
    setLocalSessionCropHistory([])
    setTempSessionCropHistory([])
    setShouldApplyToAllSessions(false)
  }

  const setRadius = (flag: boolean) => {
    const parent = document.querySelector(".BodyContentCont")
    if (flag) {
      if (parent instanceof HTMLElement) {
        parent.style.borderRadius = "0px"
      }
    } else {
      if (parent instanceof HTMLElement) {
        parent.style.borderRadius = "8px"
      }
    }
  }

  useEffect(() => {
    setRadius(true)

    return () => {
      setRadius(false)
      setMagnification(Magnification.INIT)
      setActivity(undefined)
      setSession(undefined)
      setPlayerRemoveDialogProps(undefined)
    }
  }, [])

  useEffect(() => {
    const idfActivity = params.get("activity")

    if (idfActivity) {
      setActivityId(Number(idfActivity))
    }
  }, [params])

  useEffect(() => {
    if (error) {
      router?.replace("/activity/list")
    }
  }, [error])

  useEffect(() => {
    if (activityInfo) {
      const dateStart = sub(new Date(activityInfo.dateStart), { minutes: 15 })
      const dateFinish = add(new Date(activityInfo.dateFinish), { minutes: 15 })
      setActivity(activityInfo)
      setTempActivity(cloneDeep(activityInfo))
      setRange({
        dateStart,
        dateFinish,
      })
    }
  }, [activityInfo])

  useEffect(() => {
    setTeamSpeedUnit(unit || [])
  }, [unit])

  useEffect(() => {
    middleOfScrollBarSetter()
  }, [timelineWidth, groupedActivityData, magnification])

  useLayoutEffect(() => {
    setTimeout(() => {
      const wrapper = document.querySelector(".simplebar-content-wrapper")
      const timeline = document.querySelector(".simplebar-horizontal > .simplebar-scrollbar")

      activityTimelineContRef.current?.recalculate()

      if (wrapper && timeline) {
        wrapper.scrollLeft = 0
        wrapper.scrollLeft =
          (middleOfScrollBar - timeline.clientWidth / 2) * (magnification || Magnification.INIT)
      }

      activityTimelineContRef.current?.recalculate()
      middleOfScrollBarSetter()
    })
  }, [magnification])

  useEffect(() => {
    if (!activityTimelineContRef.current) {
      return
    }

    const wrapper = document.querySelector(".simplebar-content-wrapper")
    const timeline = document.querySelector(".simplebar-horizontal > .simplebar-scrollbar")

    activityTimelineContRef.current?.recalculate()

    if (wrapper && timeline) {
      wrapper.scrollLeft = 0
      wrapper.scrollLeft =
        (middleOfScrollBar - timeline.clientWidth / 2) * (magnification || Magnification.INIT)
    }

    activityTimelineContRef.current?.recalculate()
    middleOfScrollBarSetter()
  }, [activityTimelineContRef.current])

  useEffect(() => {
    if (sessionDataList && sessionDataList.length > 0) {
      const flattened = sessionDataList.flat().filter(Boolean) as SessionData[]
      // 스토어 값은 getState로 읽고 deps에서 제외 — 스토어 set이 이 effect를 재트리거해
      // 쿼리 N개 동시 refetch 시 setState 연쇄(Maximum update depth)로 이어지는 것을 차단
      const stored = createSessionStore.getState().sessionDataList

      if (!isEqual(flattened, stored.filter(Boolean))) {
        setSessionDataList(flattened)
      }
    }
  }, [sessionDataList])

  useEffect(() => {
    if (!activityTimelineContRef.current) {
      return
    }

    setTimeout(() => {
      activityTimelineContRef.current?.recalculate()
      middleOfScrollBarSetter()
    }, 100)
  }, [isUpsertDialogOpen])

  return (
    <>
      <div className="ActivityDetailPageCont">
        <ActivityDetailHeader
          sessionLength={sessionList?.length || 0}
          playerLength={playerList?.length || 0}
          isCropping={isCropping}
          isCropSaveDisabled={
            localSessionCropHistory.length === 0 || isDeletePending || isCreatePending
          }
          isCropDirty={localSessionCropHistory.length > 0 || tempSessionCropHistory.length > 0}
          isApplyToAllSessionsActive={shouldApplyToAllSessions}
          setIsCropping={setIsCropping}
          onClickSaveCrop={onClickSaveCrop}
          onClickApplyToAllSessions={onClickApplyToAllSessions}
          resetCropData={resetLocalCropData}
          activityId={activityId}
          allDataPlayers={allDataPlayers}
          selectedPlayerList={selectedPlayerList}
          setSelectedPlayerList={setSelectedPlayerList}
        />
        {!isCropping && (
          <>
            <ActivityDetailMode
              activityInfo={activityInfo}
              sessionList={sessionList}
              middleOfScrollBarSetter={middleOfScrollBarSetter}
              activityTimelineContRef={activityTimelineContRef}
              playerSessionDataMap={sessionDataMap}
              groupedActivityData={filteredGroupedActivityData}
              teamDeviceList={teamDeviceList}
              playerList={playerList}
              onEditClick={() => {
                setIsActivityDialogOpen(true)
              }}
            />
            <SessionSelector
              sessionList={sessionList}
              onSessionClick={(session) => {
                if (selectedSession?.idfSession === (session as Session).idfSession) {
                  setSession(undefined)
                  setTemporarySession(undefined)

                  return
                }

                if (
                  selectedSession &&
                  temporarySession &&
                  (temporarySession.dateStart !== selectedSession.dateStart ||
                    temporarySession.dateFinish !== selectedSession.dateFinish)
                ) {
                  setConfirmSaveDialogOpen(true)

                  return
                }

                setSession(session as Session)
                setTemporarySession(session as Session)
              }}
              onCopyClick={(session) => setCopySessionSource(session)}
            />
          </>
        )}
        {isCropping && (
          <ActivityCropMode
            activityInfo={activityInfo}
            sessionList={sessionList}
            middleOfScrollBarSetter={middleOfScrollBarSetter}
            activityTimelineContRef={activityTimelineContRef}
            playerSessionDataMap={sessionDataMap}
            sessionDataList={sessionDataList}
            groupedActivityData={filteredGroupedActivityData}
            teamDeviceList={teamDeviceList}
            localSessionCropHistory={localSessionCropHistory}
            setLocalSessionCropHistory={setLocalSessionCropHistory}
            tempSessionCropHistory={tempSessionCropHistory}
            setTempSessionCropHistory={setTempSessionCropHistory}
          />
        )}
      </div>
      <UpsertActivityDialog
        open={isActivityDialogOpen}
        onClose={() => setIsActivityDialogOpen(false)}
        isDetail
      />
      <UpsertSessionDialog open={!!isUpsertDialogOpen} onClose={() => setUpsertDialogOpen(false)} />
      <ConfirmSaveSessionDialog isCropping={isCropping} />
      <PlayerTempMatchingDialog activityDataList={activityDataList} />
      <PlayerDeleteConfirmDialog />
      <CopySessionDialog
        open={!!copySessionSource}
        onClose={() => setCopySessionSource(null)}
        sourceSession={copySessionSource}
        idfActivity={activityId as number}
      />
    </>
  )
}
